// src/components/operationCenter/widgets/SignalChartWidget.tsx
// Generic line chart widget for quantitative trading signals.
// Styled identically to the Hurst Exponent widget: area line, dataZoom slider,
// Month-Day x-axis labels, and "Day Month Year — HH:MM" tooltip/slider dates.

import React, { useMemo } from "react";
import { Activity } from "lucide-react";
import type { EChartsOption } from "echarts";
import type { WidgetProps } from "@/types/dashboard";
import type { SignalChartConfig } from "@/types/signals";
import { useSignalSeries } from "@/hooks/signals/useSignalSeries";
import { SIGNAL_MAP } from "@/constants/signals";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import {
  createGrid,
  createXAxis,
  createTooltip,
  CHART_COLORS,
} from "@/utils/charts/theme";
import { formatDateLabel } from "@/utils/charts/dataUtils";

function formatTooltipDate(raw: string): string {
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year} — ${hh}:${mm}`;
}

const SignalChartWidget: React.FC<WidgetProps> = ({ widget }) => {
  const config = widget.config as SignalChartConfig;
  const signalName = config.signal_name;
  const definition = SIGNAL_MAP.get(widget.type);
  const label = definition?.label ?? signalName;

  const { data, isLoading, error } = useSignalSeries({
    signalName,
    ...(config.run_id != null ? { runId: config.run_id } : {}),
    ...(config.raw_feature != null ? { rawFeature: config.raw_feature } : {}),
  });

  const { dates, values, currentValue } = useMemo(() => {
    if (!data?.data || data.data.length === 0) {
      return { dates: [] as string[], values: [] as (number | null)[], currentValue: null };
    }
    const points = data.data.slice(-100);
    return {
      dates: points.map((p) => p.ts),
      values: points.map((p) => p.signal_value),
      currentValue: points[points.length - 1]?.signal_value ?? null,
    };
  }, [data]);

  const chartOption = useMemo((): EChartsOption => {
    if (dates.length === 0) return {};

    const color = CHART_COLORS.series[0]; // sky blue

    return {
      backgroundColor: "transparent",
      tooltip: createTooltip({
        trigger: "axis",
        formatter: (params: unknown) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          const p = params[0] as { axisValue: string; value: number | null };
          const val = p.value;
          if (val == null) return "";
          const valColor = val >= 0 ? "#22c55e" : "#ef4444";
          return `<div style="font-weight:500;margin-bottom:2px;color:#9ca3af;font-size:10px;">${formatTooltipDate(p.axisValue)}</div>
            <div style="font-weight:600;color:${valColor};font-size:11px;">${val.toFixed(4)}</div>`;
        },
      }),
      grid: createGrid({
        top: 8,
        right: 12,
        bottom: 50,
        left: 12,
        containLabel: true,
      }),
      dataZoom: [
        { type: "inside" },
        {
          type: "slider",
          show: true,
          bottom: 8,
          height: 18,
          borderColor: "#374151",
          textStyle: { color: "#9ca3af", fontSize: 9 },
          brushSelect: false,
          labelFormatter: (_value: number, valueStr: string) =>
            formatTooltipDate(valueStr),
        },
      ],
      xAxis: {
        ...createXAxis({ data: dates, boundaryGap: false }),
        axisLabel: {
          color: CHART_COLORS.axis.label,
          margin: 14,
          showMaxLabel: true,
          formatter: (val: string) => formatDateLabel(val, "short"),
        },
      },
      yAxis: {
        type: "value",
        scale: true,
        axisLabel: {
          color: CHART_COLORS.axis.label,
          fontSize: 9,
          formatter: (v: number) => v.toFixed(2),
        },
        splitLine: { show: false },
        axisLine: { show: false },
      },
      series: [
        {
          type: "line",
          data: values,
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 1.5, color },
          itemStyle: { color },
          areaStyle: { color: "rgba(56,189,248,0.09)" },
        },
      ],
    } as EChartsOption;
  }, [dates, values]);

  const formattedValue = currentValue != null ? currentValue.toFixed(4) : "—";
  const valueColor =
    currentValue != null && currentValue >= 0 ? "text-emerald-400" : "text-red-400";

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (error || dates.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 p-4">
        <Activity className="w-8 h-8 mb-2 opacity-50" />
        <div className="text-sm">No signal data available</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        <div className="flex items-center gap-2 min-w-0">
          <Activity className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="text-xs uppercase tracking-wide text-slate-300 truncate">
            {label}
          </span>
        </div>
        <span className={`text-xs font-semibold tabular-nums ${valueColor}`}>
          {formattedValue}
        </span>
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-0">
        <EChartsRenderer
          chartOption={chartOption}
          height="100%"
          hasData={dates.length > 0}
          containerClassName=""
        />
      </div>
    </div>
  );
};

export default SignalChartWidget;
